<script setup>
// Publicar › Voz (Fase 5, tela 07a, spec §4.9): os números de voz do assistente — pausar/ativar, rollout %, saudação
// (em horário / fora dele), horário de atendimento e a voz PELO NOME do catálogo (o fornecedor nunca aparece).
// "Testar voz" chega numa fase posterior (UpcomingButton). Número e runtime não são editáveis pela conta.
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import { Label } from 'dashboard/components-next/polaris/ui/label';
import { Switch } from 'dashboard/components-next/polaris/ui/switch';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import UpcomingButton from 'dashboard/components-next/polaris/cards/UpcomingButton.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PolarisVoiceAPI from 'dashboard/api/polaris/voiceBindings';

const { t } = useI18n();
const route = useRoute();
const { isAdmin } = useAdmin();

const assistantId = computed(() => Number(route.params.assistantId));
const bindings = ref([]);
const voices = ref([]);
const isLoading = ref(true);
const savingId = ref(null);

const DAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const voiceOptions = computed(() =>
  voices.value.map(voice => ({
    value: String(voice.id),
    label: `${voice.name} · ${voice.language}`,
  }))
);

const load = async () => {
  isLoading.value = true;
  try {
    const [{ data: bindingsData }, { data: catalogData }] = await Promise.all([
      PolarisVoiceAPI.index(assistantId.value),
      PolarisVoiceAPI.catalog(),
    ]);
    bindings.value = bindingsData.payload.map(binding => ({
      ...binding,
      form: {
        active: binding.status === 'active',
        rollout: binding.rollout_percentage ?? 100,
        greetingIn: binding.greeting?.in_hours || '',
        greetingOut: binding.greeting?.out_of_hours || '',
        days: binding.business_hours?.days || [1, 2, 3, 4, 5],
        startHour: binding.business_hours?.start_hour ?? 8,
        endHour: binding.business_hours?.end_hour ?? 18,
        voiceId: String(binding.voice_catalog_entry_id || ''),
      },
    }));
    voices.value = catalogData.payload;
  } catch {
    useAlert(t('POLARIS.VOICE_PAGE.API_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const toggleDay = (binding, day, checked) => {
  const days = new Set(binding.form.days);
  if (checked) days.add(day);
  else days.delete(day);
  binding.form.days = [...days].sort();
};

const save = async binding => {
  savingId.value = binding.id;
  const form = binding.form;
  try {
    await PolarisVoiceAPI.update(assistantId.value, binding.id, {
      status: form.active ? 'active' : 'paused',
      rollout_percentage: Number(form.rollout),
      greeting: { in_hours: form.greetingIn, out_of_hours: form.greetingOut },
      business_hours: {
        days: form.days,
        start_hour: Number(form.startHour),
        end_hour: Number(form.endHour),
      },
      voice_catalog_entry_id: form.voiceId || undefined,
    });
    useAlert(t('POLARIS.VOICE_PAGE.SAVED'));
  } catch {
    useAlert(t('POLARIS.VOICE_PAGE.API_ERROR'));
  } finally {
    savingId.value = null;
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('POLARIS.VOICE_PAGE.TITLE')"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #headerActions>
      <UpcomingButton
        :label="t('POLARIS.VOICE_PAGE.TEST_VOICE')"
        :hint="t('POLARIS.VOICE_PAGE.TEST_VOICE_HINT')"
      />
    </template>
    <template #body>
      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner />
      </div>
      <p
        v-else-if="!bindings.length"
        class="m-0 text-sm text-polaris-muted-foreground"
      >
        {{ t('POLARIS.VOICE_PAGE.EMPTY') }}
      </p>
      <template v-else>
        <section
          v-for="binding in bindings"
          :key="binding.id"
          class="flex flex-col gap-4 rounded-polaris-lg border border-solid border-polaris-border bg-polaris-card p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <h2 class="m-0 text-base font-semibold text-polaris-foreground">
                {{ binding.number || t('POLARIS.VOICE_PAGE.NUMBER_UNKNOWN') }}
              </h2>
              <p class="m-0 text-xs text-polaris-muted-foreground">
                {{ binding.mode }}
              </p>
            </div>
            <label
              class="flex items-center gap-2 text-sm text-polaris-foreground"
            >
              <Switch
                v-model="binding.form.active"
                :disabled="!isAdmin"
                :aria-label="t('POLARIS.VOICE_PAGE.TOGGLE')"
              />
              {{
                binding.form.active
                  ? t('POLARIS.VOICE_PAGE.ACTIVE')
                  : t('POLARIS.VOICE_PAGE.PAUSED')
              }}
            </label>
          </div>

          <div class="flex flex-wrap gap-4">
            <div class="flex flex-col gap-1.5">
              <Label>{{ t('POLARIS.VOICE_PAGE.VOICE') }}</Label>
              <OptionSelect
                v-model="binding.form.voiceId"
                :options="voiceOptions"
                :placeholder="t('POLARIS.VOICE_PAGE.VOICE_DEFAULT')"
                :disabled="!isAdmin"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <Label :for="`rollout-${binding.id}`">
                {{ t('POLARIS.VOICE_PAGE.ROLLOUT') }}
              </Label>
              <div class="flex items-center gap-1 text-sm">
                <Input
                  :id="`rollout-${binding.id}`"
                  v-model="binding.form.rollout"
                  class="w-20"
                  type="number"
                  min="1"
                  max="100"
                  :disabled="!isAdmin"
                />
                %
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <Label>{{ t('POLARIS.VOICE_PAGE.GREETING_IN') }}</Label>
            <Textarea
              v-model="binding.form.greetingIn"
              rows="2"
              :disabled="!isAdmin"
            />
            <Label>{{ t('POLARIS.VOICE_PAGE.GREETING_OUT') }}</Label>
            <Textarea
              v-model="binding.form.greetingOut"
              rows="2"
              :disabled="!isAdmin"
            />
            <p class="m-0 text-xs text-polaris-muted-foreground">
              {{ t('POLARIS.VOICE_PAGE.GREETING_HINT') }}
            </p>
          </div>

          <div class="flex flex-col gap-2">
            <Label>{{ t('POLARIS.VOICE_PAGE.HOURS') }}</Label>
            <div class="flex flex-wrap items-center gap-3 text-sm">
              <label
                v-for="(day, index) in DAY_KEYS"
                :key="day"
                class="flex items-center gap-1 text-polaris-foreground"
              >
                <Checkbox
                  :model-value="binding.form.days.includes(index)"
                  :disabled="!isAdmin"
                  @update:model-value="
                    checked => toggleDay(binding, index, checked)
                  "
                />
                {{ t(`POLARIS.VOICE_PAGE.DAYS.${day}`) }}
              </label>
              <span class="flex items-center gap-1">
                <Input
                  v-model="binding.form.startHour"
                  class="w-16"
                  type="number"
                  min="0"
                  max="23"
                  :disabled="!isAdmin"
                  :aria-label="t('POLARIS.VOICE_PAGE.HOURS_FROM')"
                />
                {{ t('POLARIS.VOICE_PAGE.HOURS_TO') }}
                <Input
                  v-model="binding.form.endHour"
                  class="w-16"
                  type="number"
                  min="1"
                  max="24"
                  :disabled="!isAdmin"
                  :aria-label="t('POLARIS.VOICE_PAGE.HOURS_UNTIL')"
                />
                <span>{{ t('POLARIS.VOICE_PAGE.HOURS_SUFFIX') }}</span>
              </span>
            </div>
          </div>

          <div v-if="isAdmin" class="flex justify-end">
            <Button
              size="sm"
              :disabled="savingId === binding.id"
              @click="save(binding)"
            >
              {{
                savingId === binding.id
                  ? t('POLARIS.VOICE_PAGE.SAVING')
                  : t('POLARIS.VOICE_PAGE.SAVE')
              }}
            </Button>
          </div>
        </section>
      </template>
    </template>
  </PageLayout>
</template>
